import { useState } from 'react';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  useBulkTaskStatus,
  useCreateTask,
  useMilestones,
  useTasks,
  useUpdateTask,
} from '../../lib/queries-s2.js';
import { formatDate } from '../../lib/format.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  StatusPill,
  useToast,
} from '../../components/ui/index.js';

const STATUS_TONES = {
  todo: 'neutral',
  in_progress: 'info',
  done: 'success',
  cancelled: 'neutral',
} as const;

/** Tasks + milestones tab with bulk-safe status updates. */
export function TasksTab({ orgId }: { orgId: string }) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('task.read');
  const canManage = hasPermission('task.manage');
  const tasks = useTasks(orgId, canRead);
  const milestones = useMilestones(orgId, canRead);
  const create = useCreateTask(orgId);
  const update = useUpdateTask(orgId);
  const bulk = useBulkTaskStatus(orgId);
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (!canRead) return <EmptyState title="No access" description="Your role cannot view tasks." />;
  if (tasks.isLoading) return <LoadingState />;
  if (tasks.isError || !tasks.data) {
    return (
      <ErrorState description="Tasks could not be loaded." onRetry={() => void tasks.refetch()} />
    );
  }

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = (status: 'done' | 'cancelled'): void => {
    void bulk
      .mutateAsync({ taskIds: [...selected], status })
      .then(({ results }) => {
        const ok = results.filter((r) => r.ok).length;
        const failed = results.length - ok;
        if (failed === 0) toast.success(`${ok} tasks updated`);
        else toast.error(`${ok} updated, ${failed} failed (see per-row state)`);
        setSelected(new Set());
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Bulk update failed'));
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-64 flex-1">
            <Input
              label="New task"
              placeholder="e.g. Confirm renewal meeting agenda"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <Button
            loading={create.isPending}
            disabled={title.trim().length < 3}
            onClick={() =>
              void create
                .mutateAsync({ title, priority: 'normal' })
                .then(() => {
                  toast.success('Task created');
                  setTitle('');
                })
                .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Failed'))
            }
          >
            Add task
          </Button>
          {selected.size > 0 && (
            <span className="flex items-center gap-2" role="status">
              <span className="text-xs text-text-muted">{selected.size} selected</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runBulk('done')}
                loading={bulk.isPending}
              >
                Mark done
              </Button>
              <Button size="sm" variant="ghost" onClick={() => runBulk('cancelled')}>
                Cancel tasks
              </Button>
            </span>
          )}
        </div>
      )}

      {tasks.data.items.length === 0 ? (
        <EmptyState title="No tasks" description="Tasks for this organisation would appear here." />
      ) : (
        <ul className="space-y-2">
          {tasks.data.items.map((task) => (
            <li
              key={task.id}
              className={`flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 ${
                task.status === 'done' || task.status === 'cancelled' ? 'opacity-60' : ''
              }`}
            >
              {canManage && (
                <input
                  type="checkbox"
                  aria-label={`Select task ${task.title}`}
                  checked={selected.has(task.id)}
                  onChange={() => toggle(task.id)}
                  className="size-4 accent-(--primary)"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-text-primary">{task.title}</p>
                <p className="text-xs text-text-muted">
                  {task.ownerDisplayName ?? 'Unowned'} · due {formatDate(task.dueDate)}
                  {task.priority === 'high' && ' · high priority'}
                </p>
              </div>
              {task.overdue && <Badge tone="error">Overdue</Badge>}
              <StatusPill tone={STATUS_TONES[task.status]} label={task.status.replace('_', ' ')} />
              {canManage && task.status === 'todo' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void update
                      .mutateAsync({
                        taskId: task.id,
                        body: { status: 'in_progress', expectedVersion: task.version },
                      })
                      .catch((err) =>
                        toast.error(err instanceof ApiError ? err.message : 'Update failed'),
                      )
                  }
                >
                  Start
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Card>
        <CardHeader title="Milestones" />
        <CardBody>
          {(milestones.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-text-muted">No milestones planned.</p>
          ) : (
            <ul className="divide-y divide-border">
              {milestones.data!.items.map((m) => (
                <li key={m.id} className="flex items-center justify-between py-2 text-sm">
                  <span>{m.title}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-text-muted">{formatDate(m.dueDate)}</span>
                    <StatusPill
                      tone={
                        m.status === 'reached'
                          ? 'success'
                          : m.status === 'missed'
                            ? 'error'
                            : 'neutral'
                      }
                      label={m.status}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
